import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Tag,
  Select,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Divider,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon } from '@chakra-ui/icons';
import { EquipmentCategoryType, EquipmentGroupType } from '../../contexts/types';
import { JIS_DEPARTMENT_CLASSES, InputLimits } from '../../contexts/constants';
import { getMultilingualDisplayName } from '../../utils/i18nDisplay';

interface EquipmentGroupSelectModalProps {
  /** モーダル表示フラグ */
  isOpen: boolean;
  /** モーダルクローズ関数 */
  onClose: () => void;
  /** 主要装置・機械データ */
  equipmentGroups: EquipmentGroupType[];
  /** 設備分類データ */
  equipmentCategories: EquipmentCategoryType[];
  /** 選択時のコールバック関数 */
  onSelect: (group: EquipmentGroupType) => void;
  /** 仮登録コールバック（未指定時は自由入力フォーム非表示） */
  onRegisterCandidate?: (name: string, nameEn: string, categoryId: string, newCategoryName?: string, newCategoryPrefix?: string, newCategoryNameEn?: string) => Promise<EquipmentGroupType | null>;
}

/**
 * 主要装置・機械選択モーダル（2ペイン: 左=設備分類、右=主要装置・機械 + 自由入力）
 */
const EquipmentGroupSelectModal: React.FC<EquipmentGroupSelectModalProps> = ({
  isOpen,
  onClose,
  equipmentGroups,
  equipmentCategories,
  onSelect,
  onRegisterCandidate,
}) => {
  const { t, i18n } = useTranslation();
  /** 検索キーワード */
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  /** 選択中の設備分類ID（null=すべて） */
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  /** 自由入力モード */
  const [isAddMode, setIsAddMode] = useState(false);

  /** 自由入力: 装置・機械名 */
  const [newGroupName, setNewGroupName] = useState('');

  /** 自由入力: 装置・機械名（英語） */
  const [newGroupNameEn, setNewGroupNameEn] = useState('');

  /** 自由入力: 設備分類ID */
  const [newCategoryId, setNewCategoryId] = useState('');

  /** 新規設備分類名（設備分類を新規追加する場合） */
  const [newCategoryName, setNewCategoryName] = useState('');

  /** 新規設備分類名（英語）（設備分類を新規追加する場合） */
  const [newCategoryNameEn, setNewCategoryNameEn] = useState('');

  /** 新規設備分類コードプレフィックス */
  const [newCategoryPrefix, setNewCategoryPrefix] = useState('');

  /** 新規設備分類: JIS部門分類コード */
  const [newJisCode, setNewJisCode] = useState('B');

  /** 新規設備分類: 数字部分 */
  const [newDetailCode, setNewDetailCode] = useState('');

  /** 登録中フラグ */
  const [isRegistering, setIsRegistering] = useState(false);

  /** 自由入力エラー */
  const [addErrors, setAddErrors] = useState<{ name?: string; nameEn?: string; category?: string; newCategoryName?: string; newCategoryNameEn?: string; newCategoryPrefix?: string }>({});

  /** モーダルが開かれた時にリセット */
  useEffect(() => {
    if (isOpen) {
      setSearchKeyword('');
      setSelectedCategoryId(null);
      setIsAddMode(false);
      setNewGroupName('');
      setNewGroupNameEn('');
      setNewCategoryId('');
      setNewCategoryName('');
      setNewCategoryNameEn('');
      setNewCategoryPrefix('');
      setNewJisCode('B');
      setNewDetailCode('');
      setAddErrors({});
      setIsRegistering(false);
    }
  }, [isOpen]);

  /** 設備分類ごとの装置・機械件数マップ */
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const grp of equipmentGroups) {
      if (grp.equipmentCategoryId) {
        counts.set(grp.equipmentCategoryId, (counts.get(grp.equipmentCategoryId) || 0) + 1);
      }
    }
    return counts;
  }, [equipmentGroups]);

  /** フィルタリングされた設備分類リスト */
  const filteredGroups = useMemo(() => {
    let groups = equipmentGroups;

    // 検索キーワードでフィルタリング（検索時は設備分類選択を無視して横断検索）
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase().trim();
      groups = groups.filter((group) =>
        getMultilingualDisplayName(group.equipmentGroupName)?.toLowerCase().includes(keyword)
      );
    } else if (selectedCategoryId) {
      // 設備分類で絞り込み（検索なしの場合のみ）
      groups = groups.filter((group) =>
        group.equipmentCategoryId === selectedCategoryId
      );
    }

    // sortOrder → 50音順でソート
    return [...groups].sort((a, b) => {
      const orderA = a.sortOrder ?? 99999;
      const orderB = b.sortOrder ?? 99999;
      if (orderA !== orderB) return orderA - orderB;
      return getMultilingualDisplayName(a.equipmentGroupName).localeCompare(getMultilingualDisplayName(b.equipmentGroupName), 'ja');
    });
  }, [equipmentGroups, searchKeyword, selectedCategoryId]);

  /**
   * 類似候補（入力中にリアルタイム表示）
   * 日本語名・英語名のいずれかが類似していれば表示する。1件につき1行にまとめず、
   * 該当した各言語の名称を併記した上で、一致した言語側だけをハイライトする（将来言語が増えても
   * 「表示する言語行を1つ増やす」だけで対応できる設計。2リストに分けると言語ごとにセクションが増え、
   * 両言語で一致した候補の重複表示/除去も必要になるため単一リスト方式を採用）
   */
  const similarGroups = useMemo(() => {
    const keywordJa = newGroupName.toLowerCase().trim();
    const keywordEn = newGroupNameEn.toLowerCase().trim();
    if (keywordJa.length < 2 && keywordEn.length < 2) return [];
    return equipmentGroups
      .filter(grp => !grp.deleted)
      .map(grp => ({
        grp,
        matchJa: keywordJa.length >= 2 && !!grp.equipmentGroupName?.ja?.toLowerCase().includes(keywordJa),
        matchEn: keywordEn.length >= 2 && !!grp.equipmentGroupName?.en?.toLowerCase().includes(keywordEn),
      }))
      .filter(({ matchJa, matchEn }) => matchJa || matchEn)
      .slice(0, 5);
  }, [newGroupName, newGroupNameEn, equipmentGroups]);

  /** 検索キーワードに一致する部分をハイライト表示 */
  const highlightText = useCallback((text: string | undefined, keyword: string) => {
    if (!text || !keyword.trim()) {
      return <>{text}</>;
    }

    const lowerText = text.toLowerCase();
    const lowerKeyword = keyword.toLowerCase().trim();
    const index = lowerText.indexOf(lowerKeyword);

    if (index === -1) {
      return <>{text}</>;
    }

    const before = text.slice(0, index);
    const match = text.slice(index, index + keyword.trim().length);
    const after = text.slice(index + keyword.trim().length);

    return (
      <>
        {before}
        <Box as="span" bg="yellow.200" fontWeight="bold">
          {match}
        </Box>
        {after}
      </>
    );
  }, []);

  /** 設備分類選択ハンドラー */
  const handleSelect = useCallback((group: EquipmentGroupType) => {
    onSelect(group);
  }, [onSelect]);

  /** 検索キーワード変更ハンドラー */
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  }, []);

  /** 設備分類名を取得（現在の言語に応じて英語名/日本語名を切り替え） */
  const getCategoryName = useCallback((categoryId?: string) => {
    if (!categoryId) return '';
    const cat = equipmentCategories.find(c => c.equipmentCategoryId === categoryId);
    if (!cat) return '';
    return getMultilingualDisplayName(cat.equipmentCategoryName);
  }, [equipmentCategories]);

  /** 自由入力モードに切り替え */
  const handleOpenAddMode = () => {
    setIsAddMode(true);
    setNewGroupName(searchKeyword.trim());
    setNewGroupNameEn('');
    setNewCategoryId(selectedCategoryId || '');
    setAddErrors({});
  };

  /** 自由入力キャンセル */
  const handleCancelAdd = () => {
    setIsAddMode(false);
    setNewGroupName('');
    setNewGroupNameEn('');
    setNewCategoryId('');
    setNewCategoryName('');
    setNewCategoryPrefix('');
    setNewJisCode('B');
    setNewDetailCode('');
    setAddErrors({});
  };

  /** 新規設備分類追加モードかどうか */
  const isNewCategory = newCategoryId === '__NEW__';

  /** 仮登録実行 */
  const handleRegister = async () => {
    if (!onRegisterCandidate) return;

    // バリデーション
    const errors: { name?: string; nameEn?: string; category?: string; newCategoryName?: string; newCategoryNameEn?: string; newCategoryPrefix?: string } = {};
    const trimmedName = newGroupName.trim();
    if (!trimmedName) {
      errors.name = t('components.EquipmentGroupSelectModal.validation.machineNameRequired');
    } else if (trimmedName.length > 100) {
      errors.name = t('components.EquipmentGroupSelectModal.validation.maxLength');
    }
    const trimmedNameEn = newGroupNameEn.trim();
    if (!trimmedNameEn) {
      errors.nameEn = t('components.EquipmentGroupSelectModal.validation.machineNameEnRequired');
    } else if (trimmedNameEn.length > 100) {
      errors.nameEn = t('components.EquipmentGroupSelectModal.validation.maxLength');
    }
    if (!newCategoryId) {
      errors.category = t('components.EquipmentGroupSelectModal.validation.categoryRequired');
    }
    if (isNewCategory) {
      const trimmedCategoryName = newCategoryName.trim();
      if (!trimmedCategoryName) {
        errors.newCategoryName = t('components.EquipmentGroupSelectModal.validation.categoryNameRequired');
      } else if (trimmedCategoryName.length > 100) {
        errors.newCategoryName = t('components.EquipmentGroupSelectModal.validation.maxLength');
      }
      const trimmedCategoryNameEn = newCategoryNameEn.trim();
      if (!trimmedCategoryNameEn) {
        errors.newCategoryNameEn = t('components.EquipmentGroupSelectModal.validation.categoryNameEnRequired');
      } else if (trimmedCategoryNameEn.length > 100) {
        errors.newCategoryNameEn = t('components.EquipmentGroupSelectModal.validation.maxLength');
      }
      const trimmedPrefix = newCategoryPrefix.trim();
      if (!trimmedPrefix) {
        errors.newCategoryPrefix = t('components.EquipmentGroupSelectModal.validation.prefixRequired');
      } else if (!/^[A-Z]\d{1,2}$/.test(trimmedPrefix)) {
        errors.newCategoryPrefix = t('components.EquipmentGroupSelectModal.validation.prefixFormat');
      }
    }

    if (Object.keys(errors).length > 0) {
      setAddErrors(errors);
      return;
    }

    setIsRegistering(true);
    setAddErrors({});

    try {
      const categoryIdToUse = isNewCategory ? '' : newCategoryId;
      const categoryNameToUse = isNewCategory ? newCategoryName.trim() : undefined;
      const categoryNameEnToUse = isNewCategory && newCategoryNameEn.trim() ? newCategoryNameEn.trim() : undefined;
      const categoryPrefixToUse = isNewCategory ? newCategoryPrefix.trim() : undefined;
      const result = await onRegisterCandidate(trimmedName, trimmedNameEn, categoryIdToUse, categoryNameToUse, categoryPrefixToUse, categoryNameEnToUse);

      if (result) {
        // 登録成功 → 選択して閉じる
        onSelect(result);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader>{t('components.EquipmentGroupSelectModal.title')}</ModalHeader>
        <ModalCloseButton />
        {/* 親モーダル(EquipmentEditModal)にネストされているため、react-remove-scrollの
            グローバルwheelハンドラが本Modalの内部スクロールを誤って無効化してしまう。
            ここでstopPropagationし、ブラウザ標準のスクロール処理に委ねることで回避する。 */}
        <ModalBody onWheel={(e) => e.stopPropagation()}>
          {!isAddMode ? (
            <>
              {/* 検索ボックス */}
              <InputGroup mb={4}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder={t('components.EquipmentGroupSelectModal.search.placeholder')}
                  value={searchKeyword}
                  onChange={handleSearchChange}
                  autoFocus
                />
              </InputGroup>

              {/* 2ペインレイアウト */}
              <HStack align="start" spacing={4}>
                {/* 左ペイン: 設備分類リスト */}
                <VStack
                  align="stretch"
                  spacing={1}
                  minW="200px"
                  maxW="200px"
                  overflowY="auto"
                  maxH="450px"
                  pr={1}
                  onWheel={(e) => e.stopPropagation()}
                >
                  {/* すべて */}
                  <Box
                    p={2}
                    borderRadius="md"
                    cursor="pointer"
                    bg={selectedCategoryId === null ? 'blue.100' : 'transparent'}
                    _hover={{ bg: selectedCategoryId === null ? 'blue.100' : 'gray.100' }}
                    onClick={() => setSelectedCategoryId(null)}
                  >
                    <HStack justifyContent="space-between">
                      <Text fontSize="sm" fontWeight={selectedCategoryId === null ? 'bold' : 'normal'}>
                        {t('common.label.all')}
                      </Text>
                      <Badge colorScheme="gray" fontSize="xs">
                        {equipmentGroups.length}
                      </Badge>
                    </HStack>
                  </Box>

                  {/* 設備分類リスト */}
                  {equipmentCategories.map(cat => (
                    <Box
                      key={cat.equipmentCategoryId}
                      p={2}
                      borderRadius="md"
                      cursor="pointer"
                      bg={selectedCategoryId === cat.equipmentCategoryId ? 'blue.100' : 'transparent'}
                      _hover={{ bg: selectedCategoryId === cat.equipmentCategoryId ? 'blue.100' : 'gray.100' }}
                      onClick={() => setSelectedCategoryId(cat.equipmentCategoryId || null)}
                    >
                      <HStack justifyContent="space-between">
                        <Text fontSize="sm" fontWeight={selectedCategoryId === cat.equipmentCategoryId ? 'bold' : 'normal'}>
                          {getCategoryName(cat.equipmentCategoryId)}
                        </Text>
                        <Badge colorScheme="gray" fontSize="xs">
                          {categoryCounts.get(cat.equipmentCategoryId || '') || 0}
                        </Badge>
                      </HStack>
                    </Box>
                  ))}
                </VStack>

                {/* 右ペイン: 主要装置・機械リスト */}
                <VStack
                  align="stretch"
                  spacing={2}
                  flex={1}
                  overflowY="auto"
                  maxH="450px"
                  pr={2}
                  onWheel={(e) => e.stopPropagation()}
                >
                  {/* 検索結果件数 */}
                  <HStack justifyContent="space-between">
                    <Text fontSize="sm" color="gray.600">
                      {searchKeyword.trim() ? (
                        <>
                          {t('components.EquipmentGroupSelectModal.search.result')}: <Badge colorScheme="blue">{filteredGroups.length}</Badge> {t('common.label.count')}
                        </>
                      ) : (
                        <>
                          <Badge colorScheme="gray">{filteredGroups.length}</Badge> {t('common.label.count')}
                        </>
                      )}
                    </Text>
                  </HStack>

                  {filteredGroups.length === 0 ? (
                    <Box p={4} textAlign="center" color="gray.500">
                      <Text>{t('components.EquipmentGroupSelectModal.search.noResults')}</Text>
                      {onRegisterCandidate && (
                        <Button
                          mt={3}
                          size="sm"
                          colorScheme="orange"
                          leftIcon={<AddIcon />}
                          onClick={handleOpenAddMode}
                        >
                          {t('components.EquipmentGroupSelectModal.button.addNew')}
                        </Button>
                      )}
                    </Box>
                  ) : (
                    filteredGroups.map((grp) => (
                      <Box
                        key={grp.equipmentGroupId}
                        p={3}
                        border="1px solid"
                        borderColor={grp.isCandidate ? 'orange.200' : 'gray.200'}
                        borderRadius="md"
                        cursor="pointer"
                        bg={grp.isCandidate ? 'orange.50' : 'transparent'}
                        _hover={{ bg: grp.isCandidate ? 'orange.100' : 'blue.50', borderColor: grp.isCandidate ? 'orange.400' : 'blue.300' }}
                        onClick={() => handleSelect(grp)}
                        transition="all 0.2s"
                      >
                        <HStack justifyContent="space-between">
                          <Box>
                            <Text fontSize="md">
                              {highlightText(getMultilingualDisplayName(grp.equipmentGroupName), searchKeyword)}
                              {grp.isCandidate && <Tag size="sm" colorScheme="orange" ml={2}>{t('common.label.candidate')}</Tag>}
                            </Text>
                            {searchKeyword.trim() && grp.equipmentCategoryId && (
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {getCategoryName(grp.equipmentCategoryId)}
                              </Text>
                            )}
                          </Box>
                        </HStack>
                      </Box>
                    ))
                  )}
                </VStack>
              </HStack>

              {/* 自由入力ボタン（検索結果がある場合はフッター付近に表示） */}
              {onRegisterCandidate && filteredGroups.length > 0 && (
                <>
                  <Divider my={3} />
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    leftIcon={<AddIcon />}
                    onClick={handleOpenAddMode}
                  >
                    {t('components.EquipmentGroupSelectModal.button.addNotInList')}
                  </Button>
                </>
              )}
            </>
          ) : (
            /* 自由入力フォーム */
            <VStack align="stretch" spacing={4}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('components.EquipmentGroupSelectModal.candidate.info')}
                </Text>
              </Alert>

              <FormControl isInvalid={!!addErrors.name}>
                <FormLabel fontWeight="bold">{t('components.EquipmentGroupSelectModal.form.machineName')}</FormLabel>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t('components.EquipmentGroupSelectModal.form.machineNamePlaceholder')}
                  maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                  autoComplete="off"
                  autoFocus
                />
                <FormErrorMessage>{addErrors.name}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!addErrors.nameEn}>
                <FormLabel fontWeight="bold">{t('components.EquipmentGroupSelectModal.form.machineNameEn')}</FormLabel>
                <Input
                  value={newGroupNameEn}
                  onChange={(e) => setNewGroupNameEn(e.target.value)}
                  placeholder={t('components.EquipmentGroupSelectModal.form.machineNameEnPlaceholder')}
                  maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                  autoComplete="off"
                />
                <FormErrorMessage>{addErrors.nameEn}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!addErrors.category}>
                <FormLabel fontWeight="bold">{t('components.EquipmentGroupSelectModal.form.category')}</FormLabel>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  {t('components.EquipmentGroupSelectModal.form.categoryNote')}
                </Text>
                <Select
                  value={newCategoryId}
                  onChange={(e) => {
                    setNewCategoryId(e.target.value);
                    if (e.target.value !== '__NEW__') {
                      setNewCategoryName('');
                      setNewCategoryPrefix('');
                      setAddErrors(prev => ({ ...prev, newCategoryName: undefined, newCategoryPrefix: undefined }));
                    }
                  }}
                  placeholder={t('components.EquipmentGroupSelectModal.form.categoryPlaceholder')}
                >
                  {equipmentCategories.map(c => (
                    <option key={c.equipmentCategoryId} value={c.equipmentCategoryId}>
                      {getCategoryName(c.equipmentCategoryId)}
                    </option>
                  ))}
                  <option value="__NEW__">{t('components.EquipmentGroupSelectModal.form.addNewCategory')}</option>
                </Select>
                <FormErrorMessage>{addErrors.category}</FormErrorMessage>
              </FormControl>

              {isNewCategory && (
                <>
                  <FormControl isInvalid={!!addErrors.newCategoryName}>
                    <FormLabel fontWeight="bold">{t('components.EquipmentGroupSelectModal.form.newCategoryName')}</FormLabel>
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder={t('components.EquipmentGroupSelectModal.form.newCategoryNamePlaceholder')}
                      maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                      autoComplete="off"
                    />
                    <FormErrorMessage>{addErrors.newCategoryName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!addErrors.newCategoryNameEn}>
                    <FormLabel fontWeight="bold">{t('components.EquipmentGroupSelectModal.form.newCategoryNameEn')}</FormLabel>
                    <Input
                      value={newCategoryNameEn}
                      onChange={(e) => setNewCategoryNameEn(e.target.value)}
                      placeholder={t('components.EquipmentGroupSelectModal.form.newCategoryNameEnPlaceholder')}
                      maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                      autoComplete="off"
                    />
                    <FormErrorMessage>{addErrors.newCategoryNameEn}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!addErrors.newCategoryPrefix}>
                    <FormLabel fontWeight="bold">{t('components.EquipmentGroupSelectModal.form.codePrefix')}</FormLabel>
                    <HStack spacing={2}>
                      <Select
                        value={newJisCode}
                        onChange={(e) => {
                          const code = e.target.value;
                          setNewJisCode(code);
                          setNewCategoryPrefix(code + newDetailCode);
                        }}
                        width="200px"
                        size="sm"
                        autoComplete="off"
                      >
                        {JIS_DEPARTMENT_CLASSES.map((cls) => (
                          <option key={cls.code} value={cls.code}>
                            {cls.code} - {cls.name}
                          </option>
                        ))}
                      </Select>
                      <Input
                        value={newDetailCode}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                          setNewDetailCode(val);
                          setNewCategoryPrefix(newJisCode + val);
                        }}
                        placeholder={t('components.EquipmentGroupSelectModal.form.codePrefixNumberPlaceholder')}
                        width="80px"
                        size="sm"
                        maxLength={2}
                        autoComplete="off"
                      />
                    </HStack>
                    <FormErrorMessage>{addErrors.newCategoryPrefix}</FormErrorMessage>
                  </FormControl>
                </>
              )}

              {/* 類似候補表示 */}
              {similarGroups.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600" mb={1}>
                    {t('components.EquipmentGroupSelectModal.similar.found')}
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {similarGroups.map(({ grp, matchJa, matchEn }) => (
                      <Box
                        key={grp.equipmentGroupId}
                        p={2}
                        bg="orange.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="orange.200"
                        cursor="pointer"
                        _hover={{ bg: 'orange.100' }}
                        onClick={() => handleSelect(grp)}
                      >
                        <HStack justifyContent="space-between" align="start">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">
                              {matchJa ? highlightText(grp.equipmentGroupName?.ja, newGroupName) : grp.equipmentGroupName?.ja}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {matchEn ? highlightText(grp.equipmentGroupName?.en, newGroupNameEn) : grp.equipmentGroupName?.en}
                            </Text>
                          </VStack>
                          <HStack>
                            {grp.isCandidate && <Tag size="sm" colorScheme="orange">{t('common.label.candidate')}</Tag>}
                            <Text fontSize="xs" color="gray.500">
                              ({getCategoryName(grp.equipmentCategoryId)})
                            </Text>
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              <HStack justifyContent="flex-end" spacing={2}>
                <Button onClick={handleCancelAdd} isDisabled={isRegistering}>
                  {t('common.button.back')}
                </Button>
                <Button
                  colorScheme="orange"
                  onClick={handleRegister}
                  isLoading={isRegistering}
                  isDisabled={isRegistering}
                >
                  {t('components.EquipmentGroupSelectModal.button.registerAsCandidate')}
                </Button>
              </HStack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {!isAddMode && <Button onClick={onClose}>{t('common.button.close')}</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EquipmentGroupSelectModal;
